import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaUser, FaSearch, FaEdit, FaCheckCircle, FaClock, FaEnvelope, FaTimes } from 'react-icons/fa';
import adminService, { LandingPageIdea, FilterCounts, PaginationData } from '../services/adminService';
import LoadingSpinner from './LoadingSpinner';
import { toast } from 'react-toastify';

const LandingPageIdeas: React.FC = () => {
    const [ideas, setIdeas] = useState<LandingPageIdea[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pageTypeFilter, setPageTypeFilter] = useState('');
    const [hasUserFilter, setHasUserFilter] = useState('');
    const [isProcessedFilter, setIsProcessedFilter] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(10);
    const [pagination, setPagination] = useState<PaginationData | null>(null);
    const [filterCounts, setFilterCounts] = useState<FilterCounts | null>(null);
    const [selectedIdea, setSelectedIdea] = useState<LandingPageIdea | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [notes, setNotes] = useState('');

    useEffect(() => {
        fetchIdeas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, perPage, searchTerm, pageTypeFilter, hasUserFilter, isProcessedFilter, sortBy, sortOrder]);

    const fetchIdeas = async () => {
        try {
            setIsLoading(true);
            const params: any = {
                page: currentPage,
                limit: perPage,
                sortBy,
                sortOrder,
                ...(searchTerm && { search: searchTerm }),
                ...(pageTypeFilter && { pageType: pageTypeFilter }),
                ...(hasUserFilter && { hasUser: hasUserFilter }),
                ...(isProcessedFilter && { isProcessed: isProcessedFilter }),
            };

            const response = await adminService.getLandingPageIdeas(params);
            const data = response.data || response;

            setIdeas(data.ideas || []);
            setPagination(data.pagination || null);
            setFilterCounts(data.filterCounts || null);
        } catch (error: any) {
            console.error('Error fetching ideas:', error);
            toast.error(error.response?.data?.message || 'Failed to load ideas');
            setIdeas([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusUpdate = async (ideaId: string, isProcessed: boolean) => {
        try {
            setIsUpdating(true);
            await adminService.updateIdeaStatus(ideaId, { isProcessed, notes });
            toast.success('Status updated successfully');
            fetchIdeas();
            setSelectedIdea(null);
            setNotes('');
        } catch (error: any) {
            console.error('Error updating status:', error);
            toast.error(error.response?.data?.message || 'Failed to update status');
        } finally {
            setIsUpdating(false);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <FaEdit className="text-yellow-500" />
                    Landing Page Ideas
                </h1>
                <p className="text-gray-600">View and manage ideas submitted from the landing page</p>
            </div>

            {/* Filter Stats */}
            {filterCounts && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-600">Total Ideas</div>
                        <div className="text-2xl font-bold text-blue-600">{filterCounts.total || 0}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-600">With User</div>
                        <div className="text-2xl font-bold text-green-600">{filterCounts.withUser || 0}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-600">Anonymous</div>
                        <div className="text-2xl font-bold text-orange-600">{filterCounts.withoutUser || 0}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-600">Unprocessed</div>
                        <div className="text-2xl font-bold text-red-600">{filterCounts.unprocessed || 0}</div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search ideas..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && setCurrentPage(1)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    {/* <select
                        value={pageTypeFilter}
                        onChange={(e) => {
                            setPageTypeFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Types</option>
                        <option value="founder">Founder</option>
                        <option value="agency">Agency</option>
                    </select> */}
                    <select
                        value={hasUserFilter}
                        onChange={(e) => {
                            setHasUserFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Users</option>
                        <option value="true">With User</option>
                        <option value="false">Anonymous</option>
                    </select>
                    <select
                        value={isProcessedFilter}
                        onChange={(e) => {
                            setIsProcessedFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Status</option>
                        <option value="false">Unprocessed</option>
                        <option value="true">Processed</option>
                    </select>
                    <select
                        value={`${sortBy}-${sortOrder}`}
                        onChange={(e) => {
                            const [field, order] = e.target.value.split('-');
                            setSortBy(field);
                            setSortOrder(order);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="createdAt-desc">Newest First</option>
                        <option value="createdAt-asc">Oldest First</option>
                        <option value="pageType-asc">Type A-Z</option>
                        <option value="pageType-desc">Type Z-A</option>
                    </select>
                </div>
            </div>

            {/* Ideas List */}
            {isLoading ? (
                <LoadingSpinner />
            ) : ideas.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                    <FaEdit className="mx-auto text-4xl text-gray-400 mb-4" />
                    <p className="text-gray-600 text-lg">No ideas found</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {ideas.map((idea) => (
                        <motion.div
                            key={idea._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-semibold ${idea.pageType === 'founder'
                                                ? 'bg-blue-100 text-blue-800'
                                                : 'bg-purple-100 text-purple-800'
                                                }`}
                                        >
                                            {idea.pageType || 'founder'}
                                        </span>
                                        {idea.isProcessed ? (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 flex items-center gap-1">
                                                <FaCheckCircle className="text-xs" /> Processed
                                            </span>
                                        ) : (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 flex items-center gap-1">
                                                <FaClock className="text-xs" /> Pending
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-gray-800 text-base leading-relaxed mb-3">{idea.idea}</p>
                                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                                        <div className="flex items-center gap-2">
                                            {idea.userId ? (
                                                <>
                                                    <FaUser className="text-blue-600" />
                                                    <span>
                                                        {idea.userId?.name || 'User'} ({idea.userId?.email || idea.email || 'No email'})
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <FaUser className="text-gray-400 opacity-50" />
                                                    <span>Anonymous</span>
                                                    {idea.email && (
                                                        <span className="flex items-center gap-1">
                                                            <FaEnvelope className="text-xs" /> {idea.email}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <FaClock className="text-xs" />
                                            <span>{formatDate(idea.createdAt)}</span>
                                        </div>
                                    </div>
                                    {idea.notes && (
                                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                            <p className="text-sm text-gray-700">
                                                <strong>Notes:</strong> {idea.notes}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2 ml-4">
                                    <button
                                        onClick={() => {
                                            setSelectedIdea(idea);
                                            setNotes(idea.notes || '');
                                        }}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                    >
                                        <FaEdit /> {idea.isProcessed ? 'Update' : 'Mark Processed'}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-6">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={!pagination.hasPrevPage}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        Previous
                    </button>
                    <span className="text-gray-600">
                        Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    <button
                        onClick={() => setCurrentPage((prev) => prev + 1)}
                        disabled={!pagination.hasNextPage}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Update Status Modal */}
            {selectedIdea && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
                    >
                        <h3 className="text-xl font-bold mb-4">Update Idea Status</h3>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Mark as Processed
                            </label>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => handleStatusUpdate(selectedIdea._id, true)}
                                    disabled={isUpdating}
                                    className={`px-4 py-2 rounded-lg font-medium ${selectedIdea.isProcessed
                                        ? 'bg-green-500 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-green-500 hover:text-white'
                                        } transition-colors`}
                                >
                                    <FaCheckCircle className="inline mr-2" />
                                    Processed
                                </button>
                                <button
                                    onClick={() => handleStatusUpdate(selectedIdea._id, false)}
                                    disabled={isUpdating}
                                    className={`px-4 py-2 rounded-lg font-medium ${!selectedIdea.isProcessed
                                        ? 'bg-yellow-500 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-yellow-500 hover:text-white'
                                        } transition-colors`}
                                >
                                    <FaTimes className="inline mr-2" />
                                    Pending
                                </button>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                rows={3}
                                placeholder="Add notes about this idea..."
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setSelectedIdea(null);
                                    setNotes('');
                                }}
                                disabled={isUpdating}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleStatusUpdate(selectedIdea._id, selectedIdea.isProcessed)}
                                disabled={isUpdating}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isUpdating ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default LandingPageIdeas;
